import { z } from 'zod';

import {
  acessosParteSchema,
  canalAuditoriaSchemaObj,
  comprovacaoCanalSchema,
  instituicaoCertificadoSchema,
  parteCertificadoSchema,
  replicaComunicadoSchema,
} from '@/shared/validation/certificadoAcessosDtoSchemas';

/**
 * Contrato do Certificado de Acessos da Sentença. Reusa os sub-schemas compartilhados em
 * `shared/validation` (rnf3) — os campos comuns têm de ter a MESMA forma nos dois documentos,
 * senão o mapper compartilhado deixaria de servir aos dois.
 *
 * Duas diferenças: não há `correios` (rn6) — e, pela mesma regra, `CORREIOS` sai do canal em
 * `comprovacao`, `auditoria` e `replicas` — e `comprovacao` traz o degrau da escadinha (`ponto`)
 * com a data do último marco, de onde sai o destaque da ENTREGA (rf2/rn3).
 */
const canalAuditoriaSentencaSchema = z.enum(['WHATSAPP', 'EMAIL']);

/**
 * `CORREIOS` fora dos TRÊS campos que trazem canal, não só da auditoria (rn6). Os sub-schemas
 * compartilhados aceitam o canal postal — é o certificado do processo que tem a seção — e reusá-los
 * cru deixava `comprovacao` e `replicas` validando um canal que este documento não possui: a
 * resposta passava no `safeParse` e chegava ao mapper, que a renderizaria.
 */
const comprovacaoCanalSentencaSchema = comprovacaoCanalSchema.extend({
  // `RCS` entra mesmo sem a sentença ir por RCS: no core, a comprovação da sentença sai do mesmo
  // `montarComprovacao` do processo, filtrada só por `!== 'CORREIOS'`, então o canal da citação
  // chega aqui. Recusá-lo derrubaria o certificado da sentença de todo processo com RCS; o painel
  // não o desenha porque as colunas da sentença são fixas (`CANAIS_COMPROVACAO_SENTENCA`).
  canal: z.enum(['WHATSAPP', 'EMAIL', 'RCS', 'PLATAFORMA']),
});

const replicaSentencaSchema = replicaComunicadoSchema.extend({
  canal: canalAuditoriaSentencaSchema,
});

const comprovacaoSentencaParteSchema = z.object({
  parteArbitralId: z.string(),
  nome: z.string(),
  documento: z.string().nullable(),
  papel: z.string(),
  ponto: z.enum(['NAO_ENVIADO', 'ENVIADO', 'ENTREGUE', 'LIDO']),
  // Sem `IDENTIDADE_REJEITADA` de propósito, ao contrário do schema do certificado da citação: o
  // veto de identidade (D15/D17, core#2033) só vale na citação. No core, a situação da sentença sai
  // de `montarComprovacaoDaSentenca` e o DTO é tipado `SituacaoParteCertificado`, que recusa o valor
  // no compilador. Se a sentença passar a vetar, os dois lados mudam juntos — este front primeiro.
  situacao: z.enum(['CONFIRMADO', 'PENDENTE']),
  ultimoMarcoEm: z.string().nullable(),
  canais: z.array(comprovacaoCanalSentencaSchema),
});

/**
 * Mesma estrutura de tentativas e eventos do certificado do processo — só o canal é estreitado
 * (rn6). `extend` em vez de redeclarar: um campo novo em evento de auditoria passa a valer nos
 * dois documentos sem ninguém lembrar de copiar.
 */
const canalAuditoriaSentencaObjSchema = canalAuditoriaSchemaObj.extend({
  canal: canalAuditoriaSentencaSchema,
});

export const certificadoAcessosSentencaDtoSchema = z.object({
  certificado: z.object({
    id: z.string(),
    codigoAutenticacao: z.string().nullable(),
    urlValidacao: z.string().nullable(),
    dataEmissao: z.string(),
    hashCertificado: z.string().nullable(),
    versaoHash: z.number(),
    totalEventos: z.number(),
    // `nullish` (e não `.nullable()` duro) em campo que o deploy em duas partes pode não trazer:
    // ausente = verificação não reportada, e a seção de segurança então não afirma nada sobre a
    // cadeia. O precedente é o `cadeiaIntegra` do certificado do processo (core#1253).
    cadeiaIntegra: z.boolean().nullish(),
    cadeiaQuebradaNaVersao: z.number().nullish(),
  }),
  processo: z.object({
    id: z.string(),
    numeroProcesso: z.string().nullable(),
    rito: z.string().nullable(),
    valorCausa: z.number().nullable(),
    dataEntrada: z.string().nullable(),
  }),
  sentenca: z.object({
    proferidaEm: z.string(),
    /** `null` = nenhuma comunicação enviada ainda (estado Vazio), não "documento sem eventos". */
    intimacaoEnviadaEm: z.string().nullable(),
  }),
  partes: z.object({
    requerentes: z.array(parteCertificadoSchema),
    requeridos: z.array(parteCertificadoSchema),
  }),
  comprovacao: z.array(comprovacaoSentencaParteSchema),
  auditoria: z.array(canalAuditoriaSentencaObjSchema),
  acessosPlataforma: z.array(acessosParteSchema),
  replicas: z.array(replicaSentencaSchema),
  // `nullish` pelo deploy em duas partes (back primeiro): sem o campo, a seção de segurança só
  // não declara a segunda cadeia — não afirma nada sobre ela.
  cadeiaDasComunicacoes: z
    .object({
      hashTopo: z.string().nullable(),
      versaoTopo: z.number().nullable(),
      totalEventos: z.number(),
      integra: z.boolean(),
      quebradaNaVersao: z.number().nullable(),
    })
    .nullish(),
  textos: z.object({
    objetivo: z.array(z.string()),
    seguranca: z.array(z.string()),
  }),
  instituicao: instituicaoCertificadoSchema,
});

export type CertificadoAcessosSentencaDto = z.infer<typeof certificadoAcessosSentencaDtoSchema>;
